import React, { useState, useRef, useEffect, Fragment } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, Card, Table, Badge, Spinner, Modal } from "react-bootstrap";
import {
  ArrowLeft,
  FileMinus,
  User,
  Mail,
  Smartphone,
  Hash,
} from "react-feather";

import { useNotification } from "../../components/notification";
import apiService from "../../services/apiService";
import { formatTime, formatAmount } from "../../utils";

const ViewOrder = () => {
  const didFetch = useRef(false);
  const { orderId } = useParams(); // Get user ID from URL (if editing)

  const { showNotification } = useNotification();
  const [order, setOrder] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(true);

  useEffect(() => {
    if (didFetch.current) return; // Prevent second call
    didFetch.current = true;

    if (orderId) {
      const fetchOrderDetails = async () => {
        setIsPageLoading(true);
        try {
          const response = await apiService.get(`/orders/${orderId}`);
          setOrder(response);
        } catch (error) {
          showNotification("danger", error || "Failed to fetch order details.");
        } finally {
          setIsPageLoading(false);
        }
      };

      fetchOrderDetails();
    }
  }, [orderId, showNotification]);

  return (
    <Fragment>
      {!isPageLoading && (!order || Object.keys(order).length === 0) && (
        <Row className="justify-content-center">
          <Col xs={10} md={8} lg={6} xl={5} xxl={4} className="text-center">
            <Card className="mt-5 mb-4 p-5">
              <FileMinus size={32} className="text-light-emphasis m-auto" />
              <h5 className="mt-3">No Order Found</h5>
              <p className="text-muted mb-0">
                Please check the Order ID and try again.
              </p>
            </Card>
            <Link to="/orders" className="link-secondary">
              <ArrowLeft size={16} className="me-1" />
              BACK TO ORDERS
            </Link>
          </Col>
        </Row>
      )}
      {!isPageLoading && (order || Object.keys(order).length > 0) && (
        <Card className="p-4">
          <Card.Body>
            <div className="text-end mb-3 mb-lg-0">
              <Link to="/orders" className="btn btn-light">
                <ArrowLeft size={18} /> Back
              </Link>
            </div>
            <div className="d-block d-md-flex align-items-center mb-2">
              <h1 className="mb-0 fs-5 me-2">Order #{order.orderNumber}</h1>
              <Badge className="text-uppercase fw-normal me-2" bg="secondary">
                Payment: {order.paymentStatus}
              </Badge>{" "}
              <Badge className="text-uppercase fw-normal">
                Order: {order.status}
              </Badge>
            </div>
            <p className="mb-0 text-light-emphasis fw-light fs-medium">
              <span className="me-2">
                Ordered on: <strong>{formatTime(order.createdAt)}</strong>
              </span>
              <span>
                Products: <strong>{order.OrderItems.length}</strong>
              </span>
            </p>
            <hr className="my-4" />

            <Row>
              <Col md={8} lg={8} xl={8}>
                <h6>Order Details</h6>
                <Table bordered responsive>
                  <thead>
                    <tr>
                      <th>Car Model</th> {/* Car Model column */}
                      <th>Product</th>
                      <th>Details</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Product Details */}
                    {order.OrderItems.map((item, index) => (
                      <Fragment key={index}>
                        {/* Group header for Car Model */}
                        <tr>
                          <td colSpan={4} className="fw-semibold">
                            <div className="d-flex align-items-center">
                              <div
                                className="product rounded-1 me-2 p-2"
                                style={{
                                  backgroundColor: item.Product.hexCode,
                                }}
                              ></div>
                              {item.carModel}
                            </div>
                          </td>
                        </tr>
                        {item.Product && (
                          <tr>
                            <td></td> {/* Empty cell for Car Model column */}
                            <td>
                              <div className="fw-semibold">
                                {item.colourType}
                              </div>
                              {item.Product.name} - {item.quantity}L
                            </td>
                            <td>{item.Product.description}</td>
                            <td>
                              <span className="currency">&#8377;</span>
                              {formatAmount(item.colorCoatTotalPrice)}
                            </td>
                          </tr>
                        )}
                        {/* Default products */}
                        {item.defaultProducts.map(
                          (product, idx) =>
                            product.pricePerQuantity !== 0 && (
                              <tr key={idx}>
                                <td></td>{" "}
                                {/* Empty cell for Car Model column */}
                                <td>
                                  <div className="fw-semibold">
                                    {product.name} - {item.quantity} Pack
                                  </div>
                                </td>
                                <td>{product.details}</td>
                                <td>
                                  <span className="currency">&#8377;</span>
                                  {formatAmount(
                                    product.pricePerQuantity * item.quantity
                                  )}
                                </td>
                              </tr>
                            )
                        )}
                      </Fragment>
                    ))}
                    <tr>
                      <td colSpan={4} className="fw-semibold">
                        Order Summary
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}></td>
                      <td>Payment Method</td>
                      <td className="text-end fw-medium">
                        {order.Payments[0].method?.toUpperCase() ||
                          "Bank Transfer"}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}></td>
                      <td>Subtotal (incl. GST)</td>
                      <td className="text-end fw-medium">
                        <span className="currency">&#8377;</span>
                        {formatAmount(order.subTotal)}
                      </td>
                    </tr>
                    {order.discountDetails.map((discount, index) => (
                      <tr key={index}>
                        <td colSpan={2}></td>
                        <td>
                          {discount.name} ({discount.code})
                        </td>
                        <td className="text-end text-danger fw-medium">
                          -<span className="currency">₹</span>
                          {formatAmount(discount.totalAmountApplied)}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={2}></td>
                      <td>GST</td>
                      <td className="text-end fw-medium">
                        <span className="currency">&#8377;</span>
                        {formatAmount(order.gst)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}></td>
                      <td>Shipping Fee</td>
                      <td className="text-end fw-medium">
                        <span className="currency">&#8377;</span>
                        {formatAmount(order.shippingFee)}
                      </td>
                    </tr>
                    <tr className="bg-light fw-semibold">
                      <td colSpan={2}></td>
                      <td>Total</td>
                      <td className="text-end">
                        <span className="currency">&#8377;</span>
                        {formatAmount(order.total)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={4} lg={4} xl={4}>
                <Card className="mb-4">
                  <Card.Body>
                    <h6 className="mb-0">Customer Details</h6>
                    <hr />
                    <div className="d-flex align-items-center text-light-emphasis">
                      <User size={18} className="me-2" />
                      {order.User.firstName} {order.User.lastName}
                    </div>
                    {order.User.gstNumber && (
                      <div className="d-flex align-items-center text-light-emphasis mt-2">
                        <Hash size={18} className="me-2" />{" "}
                        {order.User.gstNumber}
                      </div>
                    )}
                  </Card.Body>
                </Card>
                <Card className="mb-4">
                  <Card.Body>
                    <h6 className="mb-0">Contact Details</h6>
                    <hr />
                    <div className="d-flex align-items-center text-light-emphasis">
                      <Mail size={16} className="me-2" />
                      {order.User.email}{" "}
                    </div>
                    {order.User.phone && (
                      <div className="d-flex align-items-center text-light-emphasis mt-2">
                        <Smartphone size={16} className="me-2" />
                        {order.User.phone}
                      </div>
                    )}
                  </Card.Body>
                </Card>
                <Card className="mb-4">
                  <Card.Body>
                    <h6 className="mb-0">Shipping Address</h6>
                    <hr />
                    <p className="text-light-emphasis mb-1">
                      {order.shippingAddress.firstName}{" "}
                      {order.shippingAddress.lastName}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      {order.shippingAddress.address1}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      {order.shippingAddress.address2}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      {order.shippingAddress.city},{" "}
                      {order.shippingAddress.state}{" "}
                      {order.shippingAddress.postalCode}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      {order.shippingAddress.country}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      Phone: {order.shippingAddress.phone}
                    </p>
                  </Card.Body>
                </Card>
                <Card className="mb-4">
                  <Card.Body>
                    <h6 className="mb-0">Billing Address</h6>
                    <hr />
                    <p className="text-light-emphasis mb-1">
                      {order.billingAddress.firstName}{" "}
                      {order.billingAddress.lastName}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      {order.billingAddress.address1}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      {order.billingAddress.address2}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      {order.billingAddress.city}, {order.billingAddress.state}{" "}
                      {order.billingAddress.postalCode}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      {order.billingAddress.country}
                    </p>
                    <p className="text-light-emphasis mb-1">
                      Phone: {order.billingAddress.phone}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      <Modal
        show={isPageLoading}
        aria-labelledby="Loading"
        className="modal-loading"
        contentClassName="bg-transparent border-0"
        centered
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

export default ViewOrder;
